import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  Ban,
  Copy,
  KeyRound,
  Link2,
  Mail,
  QrCode,
  RefreshCw,
  ShieldCheck,
  Stethoscope,
  UserPlus,
  UserRound
} from 'lucide-react'
import QRCode from 'qrcode'
import toast from 'react-hot-toast'

import { useAuth } from '@/context/AuthContext'
import {
  createDoctorAccess,
  listDoctorAccessesForPatient,
  revokeDoctorAccess,
  type DoctorAccessCredentials
} from '@/lib/doctor-access'
import type { DoctorAccessGrant } from '@/types/health'

interface DoctorFormState {
  doctorName: string
  doctorEmail: string
  doctorSpecialty: string
}

const INITIAL_FORM: DoctorFormState = {
  doctorName: '',
  doctorEmail: '',
  doctorSpecialty: ''
}

function formatAccessDate(value: string | null | undefined) {
  if (!value) {
    return 'Date non renseignee'
  }

  const parsed = new Date(value)

  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return format(parsed, 'd MMM yyyy', { locale: fr })
}

function getGrantStatusLabel(grant: DoctorAccessGrant) {
  if (!grant.is_active || grant.revoked_at) {
    return 'Revoque'
  }

  return 'Actif'
}

export default function DoctorAccessManager() {
  const { user, profile } = useAuth()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<DoctorFormState>(INITIAL_FORM)
  const [latestGrant, setLatestGrant] = useState<DoctorAccessGrant | null>(null)
  const [latestCredentials, setLatestCredentials] = useState<DoctorAccessCredentials | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string>('')

  const { data: doctorAccesses = [], isLoading, isFetching } = useQuery({
    queryKey: ['doctor-accesses', user?.id],
    queryFn: () => listDoctorAccessesForPatient(user!.id),
    enabled: Boolean(user)
  })

  useEffect(() => {
    let cancelled = false

    async function buildQrCode() {
      if (!latestCredentials?.loginUrl) {
        setQrDataUrl('')
        return
      }

      try {
        const dataUrl = await QRCode.toDataURL(latestCredentials.loginUrl, {
          width: 180,
          margin: 1,
          color: {
            dark: '#e2e8f0',
            light: '#0000'
          }
        })

        if (!cancelled) {
          setQrDataUrl(dataUrl)
        }
      } catch (error) {
        console.warn('Generation locale du QR code impossible:', error)

        if (!cancelled) {
          setQrDataUrl('')
        }
      }
    }

    void buildQrCode()

    return () => {
      cancelled = true
    }
  }, [latestCredentials])

  const createMutation = useMutation({
    mutationFn: () =>
      createDoctorAccess({
        patientId: user!.id,
        patientDisplayName: profile?.full_name?.trim() || 'Camille',
        doctorName: form.doctorName,
        doctorEmail: form.doctorEmail,
        doctorSpecialty: form.doctorSpecialty || null
      }),
    onSuccess: ({ grant, credentials }) => {
      setLatestGrant(grant)
      setLatestCredentials(credentials)
      setForm(INITIAL_FORM)
      queryClient.invalidateQueries({ queryKey: ['doctor-accesses', user?.id] })
      toast.success('Acces medecin cree')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Creation de l acces impossible')
    }
  })

  const revokeMutation = useMutation({
    mutationFn: (grant: DoctorAccessGrant) => revokeDoctorAccess(grant),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctor-accesses', user?.id] })
      toast.success('Acces medecin revoque')
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Revocation impossible')
    }
  })

  async function copyValue(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value)
      toast.success(`${label} copie`)
    } catch (error) {
      console.error('Copie impossible:', error)
      toast.error(`Impossible de copier ${label.toLowerCase()}`)
    }
  }

  const hasValidForm =
    Boolean(form.doctorName.trim()) &&
    Boolean(form.doctorEmail.trim()) &&
    /\S+@\S+\.\S+/.test(form.doctorEmail.trim())

  return (
    <section id="doctors" className="space-y-6">
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Acces medecin</h2>
              <p className="text-sm text-slate-400">Creer, copier ou revoquer un acces.</p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['doctor-accesses', user?.id] })}
            className="btn-secondary"
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Rafraichir
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-cyan-500/15 text-cyan-300">
              <UserPlus className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Ajouter un medecin</h3>
              <p className="text-sm text-slate-400">Nom, email, specialite.</p>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm text-slate-300">Nom du medecin</label>
              <div className="relative">
                <UserRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={form.doctorName}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, doctorName: event.target.value }))
                  }
                  className="input pl-10"
                  placeholder="Dr Martin"
                />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm text-slate-300">Email du medecin</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  value={form.doctorEmail}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, doctorEmail: event.target.value }))
                  }
                  className="input pl-10"
                  placeholder="medecin@cabinet.fr"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="mb-2 block text-sm text-slate-300">Specialite</label>
              <div className="relative">
                <Stethoscope className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={form.doctorSpecialty}
                  onChange={(event) =>
                    setForm((current) => ({ ...current, doctorSpecialty: event.target.value }))
                  }
                  className="input pl-10"
                  placeholder="Gastroenterologie"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => createMutation.mutate()}
              className="btn-primary"
              disabled={!hasValidForm || createMutation.isPending || !user}
            >
              <KeyRound className="h-4 w-4" />
              {createMutation.isPending ? 'Generation...' : 'Generer les identifiants'}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300">
              <QrCode className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Dernier acces genere</h3>
              <p className="text-sm text-slate-400">Identifiant, code, lien, QR.</p>
            </div>
          </div>

          {latestCredentials && latestGrant ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                <p className="text-sm font-semibold text-white">{latestGrant.doctor_name}</p>
                <p className="mt-1 text-xs text-slate-400">
                  {latestGrant.doctor_specialty || 'Specialite non renseignee'}
                </p>
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Identifiant</p>
                      <p className="mt-1 break-all text-sm text-slate-100">{latestCredentials.loginIdentifier}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyValue(latestCredentials.loginIdentifier, 'Identifiant')}
                      className="btn-ghost"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Code d acces</p>
                      <p className="mt-1 text-lg font-semibold tracking-[0.18em] text-white">
                        {latestCredentials.accessCode}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyValue(latestCredentials.accessCode, 'Code')}
                      className="btn-ghost"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Lien direct</p>
                      <p className="mt-1 break-all text-sm text-slate-100">{latestCredentials.loginUrl}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => copyValue(latestCredentials.loginUrl, 'Lien')}
                      className="btn-ghost"
                    >
                      <Link2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-dark-border bg-dark-surface-high p-4">
                <p className="text-xs uppercase tracking-[0.18em] text-slate-500">QR code</p>
                <div className="mt-4 flex items-center justify-center rounded-2xl border border-dashed border-dark-border bg-slate-950/50 p-4">
                  {qrDataUrl ? (
                    <img src={qrDataUrl} alt="QR code acces medecin" className="h-44 w-44 rounded-xl" />
                  ) : (
                    <div className="text-sm text-slate-500">QR code indisponible</div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-2xl border border-dark-border bg-dark-surface-high p-6 text-sm text-slate-400">
              Aucun identifiant genere pour le moment.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white">Medecins autorises</h3>
            <p className="text-sm text-slate-400">Acces actifs et revoques.</p>
          </div>
          <span className="rounded-full border border-dark-border px-3 py-1 text-xs text-slate-300">
            {doctorAccesses.length} acces
          </span>
        </div>

        {isLoading ? (
          <div className="mt-6 flex items-center justify-center py-10">
            <div className="spinner" />
          </div>
        ) : doctorAccesses.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dark-border bg-dark-surface-high p-6 text-sm text-slate-400">
            Aucun acces medecin enregistre.
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {doctorAccesses.map((grant) => {
              const isRevoked = !grant.is_active || Boolean(grant.revoked_at)

              return (
                <div
                  key={grant.id}
                  className="rounded-2xl border border-dark-border bg-dark-surface-high p-4"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-base font-semibold text-white">{grant.doctor_name}</p>
                        <span
                          className={`rounded-full border px-2.5 py-1 text-[11px] ${
                            isRevoked
                              ? 'border-red-500/30 bg-red-500/10 text-red-100'
                              : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-100'
                          }`}
                        >
                          {getGrantStatusLabel(grant)}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-3 text-sm text-slate-300 md:grid-cols-2">
                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Specialite</p>
                          <p className="mt-1">{grant.doctor_specialty || 'Non renseignee'}</p>
                        </div>

                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Email</p>
                          <p className="mt-1 break-all">{grant.doctor_email}</p>
                        </div>

                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Identifiant</p>
                          <p className="mt-1 break-all">{grant.login_identifier}</p>
                        </div>

                        <div>
                          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Creation</p>
                          <p className="mt-1">{formatAccessDate(grant.created_at)}</p>
                        </div>
                      </div>

                      <div className="mt-3 text-xs text-slate-500">
                        Apercu du code: ...{grant.access_code_preview || '----'}
                        {grant.revoked_at ? ` - Revoque le ${formatAccessDate(grant.revoked_at)}` : ''}
                      </div>
                    </div>

                    {!isRevoked && (
                      <button
                        type="button"
                        onClick={() => revokeMutation.mutate(grant)}
                        className="btn-secondary text-red-200"
                        disabled={revokeMutation.isPending}
                      >
                        <Ban className="h-4 w-4" />
                        Revoquer
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </section>
  )
}
